import { SortPayload } from 'react-admin';

/**
 * Paginate an array of items.
 *
 * Some of our heimdall APIs do not support server-side pagination.
 * In those cases, we fetch all items and paginate them client-side using this function.
 *
 * @param items - Array of items to paginate.
 * @param page - Current page number.
 * @param perPage - Number of items per page.
 * @returns A new array containing only the items for the current page.
 */
export const clientPaginate = <I>(
  items: I[],
  page: number = 1,
  perPage: number = 25,
): I[] => {
  const start = (page - 1) * perPage;
  const end = start + perPage;
  return items.slice(start, end);
};

/**
 * Sort an array of items by a specified field.
 *
 * Some of our heimdall APIs do not support server-side sorting.
 * In those cases, we sort all items client-side using this function.
 *
 * @param items - Array of items to sort.
 * @param getField - Function that returns the field to sort by.
 * @param order - Sorting order.
 * @returns A new array sorted by the specified field.
 */
export const clientSort = <T>(
  items: T[],
  getField: (item: T) => boolean | Date | number | null | string | undefined,
  order: SortPayload['order']
): T[] => {
  const isNullOrUndefined = (v: unknown): v is null | undefined => v === null || v === undefined;

  return items.slice().sort((a, b) => {
    let aValue = getField(a);
    let bValue = getField(b);

    const aIsNil = isNullOrUndefined(aValue);
    const bIsNil = isNullOrUndefined(bValue);

    if (aIsNil && bIsNil) return 0;
    if (aIsNil) return 1;
    if (bIsNil) return -1;

    if (aValue instanceof Date) aValue = aValue.getTime();
    if (bValue instanceof Date) bValue = bValue.getTime();

    if (typeof aValue === 'boolean') aValue = aValue ? 1 : 0;
    if (typeof bValue === 'boolean') bValue = bValue ? 1 : 0;

    const aComparable = String(aValue);
    const bComparable = String(bValue);

    if (aComparable < bComparable) return order === 'ASC' ? -1 : 1;
    if (aComparable > bComparable) return order === 'ASC' ? 1 : -1;
    return 0;
  });
};